import { useMemo } from "react";
import {
  Autocomplete,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";

const DURATION_OPTIONS = ["weekly", "monthly", "quarterly", "yearly", "custom"];

const getDateRange = (duration, referenceDate = new Date()) => {
  const d = new Date(referenceDate);
  let start, end;
  switch (duration) {
    case "weekly": {
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      start = new Date(d.getFullYear(), d.getMonth(), diff);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      break;
    }
    case "monthly":
      start = new Date(d.getFullYear(), d.getMonth(), 1);
      end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      break;
    case "quarterly": {
      const q = Math.floor(d.getMonth() / 3);
      start = new Date(d.getFullYear(), q * 3, 1);
      end = new Date(d.getFullYear(), q * 3 + 3, 0);
      break;
    }
    case "yearly":
      start = new Date(d.getFullYear(), 0, 1);
      end = new Date(d.getFullYear(), 11, 31);
      break;
    default:
      start = new Date(d.getFullYear(), d.getMonth(), 1);
      end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  }
  return { start, end };
};

const formatDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const DashboardFilters = ({
  projects,
  typeList,
  filters,
  onFiltersChange,
}) => {
  const { t } = useTranslation();
  const isCustom = filters.duration === "custom";

  const handleProjectChange = (_, value) => {
    onFiltersChange({ ...filters, projectIds: value.map((p) => p.id) });
  };

  const handleTypeChange = (e) => {
    onFiltersChange({ ...filters, typeId: e.target.value || "" });
  };

  const handleDurationChange = (e) => {
    const duration = e.target.value;
    if (duration === "custom") {
      onFiltersChange({ ...filters, duration });
      return;
    }
    // Calculate new range based on the currently selected start date
    const referenceDate = filters.startDate
      ? new Date(filters.startDate)
      : new Date();
    const range = getDateRange(duration, referenceDate);
    onFiltersChange({
      ...filters,
      duration,
      startDate: formatDate(range.start),
      endDate: formatDate(range.end),
    });
  };

  const navigatePeriod = (direction) => {
    const current = new Date(filters.startDate);
    let next;
    switch (filters.duration) {
      case "weekly":
        next = new Date(current);
        next.setDate(current.getDate() + 7 * direction);
        break;
      case "monthly":
        next = new Date(current);
        next.setMonth(current.getMonth() + direction);
        break;
      case "quarterly":
        next = new Date(current);
        next.setMonth(current.getMonth() + 3 * direction);
        break;
      case "yearly":
        next = new Date(current);
        next.setFullYear(current.getFullYear() + direction);
        break;
      default:
        next = current;
    }
    const range = getDateRange(filters.duration, next);
    onFiltersChange({
      ...filters,
      startDate: formatDate(range.start),
      endDate: formatDate(range.end),
    });
  };

  const handleStartDateChange = (date) => {
    if (date) {
      onFiltersChange({
        ...filters,
        duration: "custom",
        startDate: formatDate(date),
      });
    }
  };

  const handleEndDateChange = (date) => {
    if (date) {
      onFiltersChange({
        ...filters,
        duration: "custom",
        endDate: formatDate(date),
      });
    }
  };

  const selectedProjects = useMemo(
    () => projects.filter((p) => filters.projectIds.includes(p.id)),
    [projects, filters.projectIds]
  );

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
      <Autocomplete
        multiple
        size="small"
        options={projects}
        getOptionLabel={(o) => o.name}
        value={selectedProjects}
        onChange={handleProjectChange}
        limitTags={2}
        sx={{ minWidth: { xs: 0, sm: 250 }, flexGrow: 1, width: { xs: "100%", sm: "auto" } }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("dashboard.filter.project")}
          />
        )}
      />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", width: { xs: "100%", sm: "auto" } }}>
        <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 150 }, flexGrow: { xs: 1, sm: 0 } }}>
          <InputLabel>{t("dashboard.filter.type")}</InputLabel>
          <Select
            value={filters.typeId}
            label={t("dashboard.filter.type")}
            onChange={handleTypeChange}
          >
            <MenuItem value="">
              <em>{t("dashboard.filter.allTypes")}</em>
            </MenuItem>
            {typeList.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 140 }, flexGrow: { xs: 1, sm: 0 } }}>
          <InputLabel>{t("dashboard.filter.duration")}</InputLabel>
          <Select
            value={filters.duration}
            label={t("dashboard.filter.duration")}
            onChange={handleDurationChange}
          >
            {DURATION_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>
                {t(`dashboard.duration.${d}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton
          size="small"
          onClick={() => navigatePeriod(-1)}
          disabled={isCustom}
        >
          <ChevronLeft />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => navigatePeriod(1)}
          disabled={isCustom}
        >
          <ChevronRight />
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}>
        <DatePicker
          label={t("dashboard.filter.startDate")}
          format="dd-MM-yyyy"
          value={filters.startDate ? new Date(filters.startDate) : null}
          onChange={handleStartDateChange}
          slotProps={{ textField: { size: "small", sx: { width: { xs: "100%", sm: 160 } } } }}
        />
        <DatePicker
          label={t("dashboard.filter.endDate")}
          format="dd-MM-yyyy"
          value={filters.endDate ? new Date(filters.endDate) : null}
          onChange={handleEndDateChange}
          slotProps={{ textField: { size: "small", sx: { width: { xs: "100%", sm: 160 } } } }}
        />
      </Box>
    </Box>
  );
};

export default DashboardFilters;
